/* Offline fallback for the birth-place field.
 *
 * The form geocodes through Geoapify. If that key is missing, the service is
 * down, or the request is rate limited, the dropdown would otherwise come back
 * empty and the calculator becomes unusable with no explanation. These cover
 * the large majority of Indian births plus the main diaspora cities, so the
 * page still works when the geocoder does not.
 *
 * tz is the standard offset in hours. India has no DST, so 5.5 is exact
 * year-round; the others are resolved properly from their IANA zone at submit.
 */
export interface City {
  formatted: string;
  lat: number;
  lon: number;
  timezone: { name: string };
}

const IN = (n: string, lat: number, lon: number): City => ({
  formatted: `${n}, India`,
  lat,
  lon,
  timezone: { name: "Asia/Kolkata" },
});

export const FALLBACK_CITIES: City[] = [
  IN("Mumbai, Maharashtra", 19.076, 72.8777),
  IN("Delhi", 28.6139, 77.209),
  IN("Bengaluru, Karnataka", 12.9716, 77.5946),
  IN("Hyderabad, Telangana", 17.385, 78.4867),
  IN("Chennai, Tamil Nadu", 13.0827, 80.2707),
  IN("Kolkata, West Bengal", 22.5726, 88.3639),
  IN("Pune, Maharashtra", 18.5204, 73.8567),
  IN("Ahmedabad, Gujarat", 23.0225, 72.5714),
  IN("Jaipur, Rajasthan", 26.9124, 75.7873),
  IN("Lucknow, Uttar Pradesh", 26.8467, 80.9462),
  IN("Kanpur, Uttar Pradesh", 26.4499, 80.3319),
  IN("Nagpur, Maharashtra", 21.1458, 79.0882),
  IN("Indore, Madhya Pradesh", 22.7196, 75.8577),
  IN("Bhopal, Madhya Pradesh", 23.2599, 77.4126),
  IN("Patna, Bihar", 25.5941, 85.1376),
  IN("Surat, Gujarat", 21.1702, 72.8311),
  IN("Vadodara, Gujarat", 22.3072, 73.1812),
  IN("Ludhiana, Punjab", 30.901, 75.8573),
  IN("Chandigarh", 30.7333, 76.7794),
  IN("Kochi, Kerala", 9.9312, 76.2673),
  IN("Thiruvananthapuram, Kerala", 8.5241, 76.9366),
  IN("Coimbatore, Tamil Nadu", 11.0168, 76.9558),
  IN("Visakhapatnam, Andhra Pradesh", 17.6868, 83.2185),
  IN("Guwahati, Assam", 26.1445, 91.7362),
  IN("Bhubaneswar, Odisha", 20.2961, 85.8245),
  IN("Varanasi, Uttar Pradesh", 25.3176, 82.9739),
  IN("Amritsar, Punjab", 31.634, 74.8723),
  IN("Dehradun, Uttarakhand", 30.3165, 78.0322),
  IN("Ranchi, Jharkhand", 23.3441, 85.3096),
  IN("Raipur, Chhattisgarh", 21.2514, 81.6296),
  { formatted: "Kathmandu, Nepal", lat: 27.7172, lon: 85.324, timezone: { name: "Asia/Kathmandu" } },
  { formatted: "Dubai, United Arab Emirates", lat: 25.2048, lon: 55.2708, timezone: { name: "Asia/Dubai" } },
  { formatted: "Singapore", lat: 1.3521, lon: 103.8198, timezone: { name: "Asia/Singapore" } },
  { formatted: "London, United Kingdom", lat: 51.5074, lon: -0.1278, timezone: { name: "Europe/London" } },
  { formatted: "New York, United States", lat: 40.7128, lon: -74.006, timezone: { name: "America/New_York" } },
  { formatted: "Toronto, Canada", lat: 43.6532, lon: -79.3832, timezone: { name: "America/Toronto" } },
  { formatted: "Sydney, Australia", lat: -33.8688, lon: 151.2093, timezone: { name: "Australia/Sydney" } },
];

export function searchCities(q: string): City[] {
  const needle = q.trim().toLowerCase();
  if (needle.length < 2) return [];
  return FALLBACK_CITIES.filter((c) =>
    c.formatted.toLowerCase().includes(needle)
  ).slice(0, 6);
}
